import { Component, Input } from '@angular/core';
import { VoyageRadarPlaybackController } from '../../playback/voyageradar-playback.controller';

/**
 * Voyage Radar "Filter Vessel Types" — the ship-type filter as a panel section.
 *
 * A peer of the ON MAP bar and Refresh History, and the middle band of the
 * three: ON MAP says what is drawn, this says which of it is visible, Refresh
 * History says what else could be.
 *
 * It reads and writes the SAME `VoyageRadarPlaybackController` the map toolbar's
 * filter drawer uses, so the two can never disagree — the toolbar's flagged dot
 * and this section's "2 of 12" are one piece of state. The controller arrives as
 * an input rather than through DI because this renders inside the host's
 * `*ngFor` over `collection.sensors`.
 *
 * Unlike the ON MAP bar this stays live during snapshot playback: `renderStep`
 * applies the filter to every frame, and `loadPlaybackSteps` seeds the
 * categories from the union of all loaded snapshots. That is the main reason
 * the filter moved out of that bar — collapsing the bar during playback used to
 * take the filter with it.
 *
 * The section renders only while `availableTypes` is non-empty, which is the
 * controller's answer to "are there vessels on the map". Keeping that honest is
 * a map-controller job, not a template one: `releaseLiveLayer` drops the
 * categories with the layer they describe, and `resetTypeFilter` drops the
 * user's selection too once the map is cleared for good.
 *
 * Change detection stays Default to match its siblings.
 */
@Component({
  selector: 'app-voyage-vessel-types',
  templateUrl: './voyage-vessel-types.component.html',
  styleUrls: ['./voyage-vessel-types.component.scss'],
})
export class VoyageVesselTypesComponent {
  /** The shared Voyage Radar toolbar controller. */
  @Input() controller!: VoyageRadarPlaybackController;

  /**
   * Collapsed by default. The header's count already says how many categories
   * are on the map, which teaches that the control exists without spending the
   * vertical space in a sidebar that has two other sections under it.
   *
   * Not persisted across a teardown on purpose: the section unmounts whenever
   * the map empties, so the next set of vessels starts collapsed rather than
   * reopening on a chip list the user has not seen.
   */
  expanded = false;

  toggle(): void {
    this.expanded = !this.expanded;
  }

  /** "12" with no filter, "2 of 12" with one. */
  summary(total: number, selected: number): string {
    return selected > 0 ? `${selected} of ${total}` : `${total}`;
  }

  onSelectionChange(selected: Set<string>): void {
    this.controller.setTypeFilter(selected);
  }

  /** Back to "show everything" — an empty set IS the no-filter state. */
  clear(event: Event): void {
    event.stopPropagation();
    this.controller.setTypeFilter(new Set<string>());
  }
}
